import { Column } from '@tanstack/react-table';
import { Check, ChevronDown } from 'lucide-react';
import pluralize from 'pluralize';
import { useState } from 'react';
import { cn } from '../../utils/cn';
import { Button } from '../button/button';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '../command-menu/command-menu-primitives';
import { Popover } from '../popover/popover';

type ColumnFilterNewProps<TData> = {
  column: Column<TData>;
  label: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  className?: string;
};

/**
 * Multi-select dropdown filter for a specific column.
 *
 * @param props.column - TanStack Table column instance
 * @param props.label - Display label for the filter
 * @param props.searchable - Whether to show search input in dropdown
 * @param props.searchPlaceholder - Placeholder for the search input
 * @param props.className - Additional classes for the button
 *
 * @example
 * ```tsx
 * const table = useReactTable({
 *   data,
 *   columns,
 *   getFacetedUniqueValues: getFacetedUniqueValues(),
 *   getFilteredRowModel: getFilteredRowModel(),
 * });
 *
 * <DataTableNew.ColumnFilter
 *   column={table.getColumn('vendor')}
 *   label="Vendor"
 *   searchable
 *   searchPlaceholder="Search vendors..."
 * />
 * ```
 */
export const ColumnFilterNew = <TData,>({
  column,
  label,
  searchable = true,
  searchPlaceholder,
  className,
}: ColumnFilterNewProps<TData>) => {
  const [searchTerm, setSearchTerm] = useState('');

  const columnUniqueValues = Array.from(column.getFacetedUniqueValues().keys()).filter((value) => Boolean(value));
  const selectedValues = new Set(column.getFilterValue() as string[]);
  const visibleLabel = selectedValues.size ? pluralize(label.toLowerCase(), selectedValues.size, true) : label;

  return (
    <Popover
      triggerElement={
        <Button
          variant="secondary"
          className={cn('group', className)}
          iconRight={<ChevronDown className="h-4 w-4" />}
          iconRightClassName="transition-transform duration-200 ease-out group-data-[state=open]:rotate-180"
        >
          {visibleLabel}
        </Button>
      }
      content={
        <Command className="flex flex-col gap-4">
          <div className={cn('flex flex-col gap-4 px-5 pt-5', !searchable && 'pt-2')}>
            {searchable && (
              <CommandInput
                placeholder={searchPlaceholder ?? `Search ${column.id}`}
                // "Controlled" value so we can reset it on Reset button click
                value={searchTerm}
                onValueChange={setSearchTerm}
              />
            )}

            <CommandList>
              <CommandGroup>
                {columnUniqueValues.map((uniqueValue) => {
                  const isSelected = selectedValues.has(uniqueValue);

                  return (
                    <CommandItem
                      key={uniqueValue}
                      onSelect={() => {
                        if (isSelected) {
                          selectedValues.delete(uniqueValue);
                        } else {
                          selectedValues.add(uniqueValue);
                        }

                        const filterValues = Array.from(selectedValues);
                        column.setFilterValue(filterValues.length ? filterValues : undefined);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={cn(
                            'flex h-4 w-4 items-center justify-center rounded-md border p-[1px]',
                            isSelected ? 'border-primary bg-primary text-black' : 'opacity-50 [&_svg]:invisible',
                          )}
                        >
                          <Check className="w-full" strokeWidth={1.4} />
                        </div>

                        <p>{uniqueValue}</p>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </div>
        </Command>
      }
      footerElement={
        <Button
          variant="outline"
          className="w-full"
          onClick={() => {
            column.setFilterValue(undefined);
            setSearchTerm('');
          }}
        >
          Reset
        </Button>
      }
      align="start"
    />
  );
};
